/**
 * Sign-out button + signed-in-as email pill.
 *
 * Shared by the editor top bar and the deck-list header so the user
 * always has a visible way out, plus confirmation of which account
 * they're using. Mirrors the cleanup pattern in pages/Dashboard.tsx
 * (Flow360 sessionStorage + Supabase signOut), so logging out from
 * the deck editor leaves the rest of the app in the same state as a
 * normal sign-out.
 */

import { useEffect, useState } from "react";

import { authClient } from "../../../lib/auth";

interface SignOutButtonProps {
  /** Lets tests inject a fixed email. Production reads from the auth session. */
  testEmail?: string | null;
}

export function SignOutButton({ testEmail }: SignOutButtonProps) {
  const [email, setEmail] = useState<string | null | undefined>(testEmail);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (testEmail !== undefined) {
      setEmail(testEmail);
      return;
    }
    let mounted = true;
    void authClient.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        setEmail(session?.user?.email ?? null);
      })
      .catch(() => {
        if (!mounted) return;
        setEmail(null);
      });
    return () => {
      mounted = false;
    };
  }, [testEmail]);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    // Same cleanup pattern Dashboard uses, so the deck editor sign-out
    // leaves auth state consistent with a sign-out from anywhere else.
    sessionStorage.removeItem("flow360_access_token");
    sessionStorage.removeItem("flow360_refresh_token");
    sessionStorage.removeItem("flow360_cognito_sub");
    sessionStorage.removeItem("flow360_user_email");
    try {
      await authClient.auth.signOut();
    } catch (err) {
      // Loud failure rather than silent — surface in console for
      // diagnosis. App-level auth state will still flip via the
      // listener even if signOut() rejected after partial cleanup.
      console.warn("[SignOutButton] signOut error:", err);
    }
    // App.tsx watches the session and re-routes to /sign-in when it
    // flips to null. Don't force-navigate from here — that races with
    // the route change and can land on a blank tab.
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
      data-testid="signout-pill"
    >
      {email ? (
        <span
          style={{ color: "var(--gray-9)", fontSize: "0.85em" }}
          data-testid="signout-email"
        >
          {email}
        </span>
      ) : null}
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        style={btnStyle}
        data-testid="signout-button"
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  fontSize: "0.85em",
  color: "var(--gray-12)",
  background: "transparent",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  padding: "0.25rem 0.55rem",
  cursor: "pointer",
};
